import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Pencil, RotateCcw, Send } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { SERVICE_LABEL, templatePath, type EmailTemplate, type EmailTemplateList, type RenderedEmail } from '../lib/email-templates';
import { formatRelative } from '../lib/format';
import { useDebouncedValue } from '../lib/hooks';
import { useToast } from './toast';
import { Alert, Badge, Button, Card, CardHeader, ConfirmDialog, ErrorState, Field, Input, Modal, PageLoader, Textarea } from './ui';

/** Settings → Email templates: the wording of every email Edvora sends, in the school's own words. */
export function EmailTemplateSettings() {
  const query = useQuery({ queryKey: ['email-templates'], queryFn: () => api.get<EmailTemplateList>('/email-templates') });
  const [editing, setEditing] = useState<EmailTemplate | null>(null);

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const { items, mailboxes } = query.data;
  const groups = [...new Set(items.map((item) => item.group))];
  const mailboxReady = mailboxes.IT.ready || !!mailboxes.FACILITIES?.ready;
  const showService = !!mailboxes.FACILITIES;

  return (
    <div className="space-y-6">
      <Alert tone="blue" icon={<Mail />} title="Every email Edvora sends, in your words">
        Change any of them to suit your school. Anything you leave alone uses Edvora’s wording. Emails are plain text, sent from the help desk mailbox, and each one
        carries {'{{variables}}'} that are filled in when it goes out.
      </Alert>

      {!mailboxReady && (
        <Alert tone="amber" title="Help desk email isn’t set up">
          These emails are sent from the help desk mailbox.{' '}
          <Link to="/integrations/email" className="font-medium underline">
            Set up help desk email
          </Link>{' '}
          to send them.
        </Alert>
      )}

      {groups.map((group) => (
        <Card key={group}>
          <CardHeader
            title={group}
            description={group === 'Help desk' ? 'Sent to requesters and technicians about tickets' : 'Sent to borrowers and families about devices'}
          />
          <ul className="divide-y divide-slate-100">
            {items
              .filter((item) => item.group === group)
              .map((item) => (
                <li key={`${item.key}:${item.service}`} className="flex flex-wrap items-center gap-3 px-5 py-3.5 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
                      {item.label}
                      {showService && <Badge tone={item.service === 'FACILITIES' ? 'violet' : 'brand'}>{SERVICE_LABEL[item.service]}</Badge>}
                      {item.customized && <Badge tone="green">Your wording</Badge>}
                    </p>
                    <p className="text-xs text-slate-500">{item.description}</p>
                    {item.customized && item.updatedAt && (
                      <p className="mt-0.5 text-xs text-slate-400">
                        Changed {formatRelative(item.updatedAt)}
                        {item.updatedByName && ` by ${item.updatedByName}`}
                      </p>
                    )}
                  </div>
                  <p className="hidden max-w-sm truncate text-xs text-slate-500 lg:block" title={item.preview.subject}>
                    {item.preview.subject}
                  </p>
                  <Button size="sm" icon={<Pencil />} onClick={() => setEditing(item)}>
                    Edit
                  </Button>
                </li>
              ))}
          </ul>
        </Card>
      ))}

      {editing && (
        <TemplateEditor
          template={editing}
          mailboxReady={editing.service === 'FACILITIES' ? !!mailboxes.FACILITIES?.ready : mailboxes.IT.ready}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function TemplateEditor({ template, mailboxReady, onClose }: { template: EmailTemplate; mailboxReady: boolean; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [resetting, setResetting] = useState(false);

  const debouncedSubject = useDebouncedValue(subject, 400);
  const debouncedBody = useDebouncedValue(body, 400);
  const preview = useQuery({
    queryKey: ['email-template-preview', template.key, template.service, debouncedSubject, debouncedBody],
    queryFn: () => api.post<RenderedEmail>(templatePath(template, '/preview'), { subject: debouncedSubject, body: debouncedBody }),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['email-templates'] });
  const save = useMutation({
    mutationFn: () => api.put<EmailTemplate>(templatePath(template), { subject, body }),
    onSuccess: () => {
      void refresh();
      toast.success(`Saved the “${template.label}” email`);
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const reset = useMutation({
    mutationFn: () => api.delete<EmailTemplate>(templatePath(template)),
    onSuccess: (saved) => {
      void refresh();
      setSubject(saved.subject);
      setBody(saved.body);
      setResetting(false);
      toast.success('Put back to Edvora’s wording');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const sendTest = useMutation({
    mutationFn: () => api.post<{ to: string; from: string }>(templatePath(template, '/test'), { subject, body }),
    onSuccess: ({ to }) => toast.success(`Sent a test to ${to}`),
    onError: (err) => toast.error(errorMessage(err)),
  });

  /** Puts a variable where the cursor is. */
  const insert = (name: string) => {
    const token = `{{${name}}}`;
    const field = bodyRef.current;
    if (!field) {
      setBody((current) => `${current}${token}`);
      return;
    }
    const start = field.selectionStart ?? body.length;
    const end = field.selectionEnd ?? start;
    setBody(`${body.slice(0, start)}${token}${body.slice(end)}`);
    requestAnimationFrame(() => {
      field.focus();
      field.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const unchanged = subject === template.subject && body === template.body;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="xl"
        title={template.label}
        description={template.description}
        footer={
          <>
            {template.customized && (
              <Button variant="ghost" icon={<RotateCcw />} onClick={() => setResetting(true)}>
                Use Edvora’s wording
              </Button>
            )}
            <Button icon={<Send />} disabled={!mailboxReady} loading={sendTest.isPending} onClick={() => sendTest.mutate()} title={mailboxReady ? undefined : 'Set up help desk email first'}>
              Send me a test
            </Button>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={unchanged} loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            <Field
              label="Subject"
              required
              hint={template.requiredInSubject.length > 0 ? `Keep ${template.requiredInSubject.map((name) => `{{${name}}}`).join(', ')} so replies find their ticket.` : undefined}
            >
              <Input value={subject} onChange={(event) => setSubject(event.target.value)} maxLength={300} className="font-mono text-xs" />
            </Field>
            <Field label="Message">
              <Textarea ref={bodyRef} rows={14} value={body} onChange={(event) => setBody(event.target.value)} maxLength={10_000} className="font-mono text-xs" />
            </Field>
            <div>
              <p className="mb-1.5 text-sm font-medium text-slate-700">What you can drop in</p>
              <div className="flex flex-wrap gap-1.5">
                {template.variables.map((variable) => (
                  <button
                    key={variable.name}
                    type="button"
                    onClick={() => insert(variable.name)}
                    title={`${variable.description}. For example: ${variable.sample}`}
                    className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-xs text-slate-700 hover:bg-brand-100 hover:text-brand-800"
                  >
                    {`{{${variable.name}}}`}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                A line whose values are all empty is left out, so optional parts disappear on their own. Edvora adds the “Reply above this line” marker to help desk
                emails.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">How it arrives</p>
            {preview.error ? (
              <Alert tone="red">{errorMessage(preview.error)}</Alert>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <p className="border-b border-slate-200 pb-2 text-sm font-semibold text-slate-900">{preview.data?.subject ?? '…'}</p>
                <pre className="mt-3 font-sans text-sm whitespace-pre-wrap text-slate-700">{preview.data?.text ?? ''}</pre>
              </div>
            )}
            <p className="text-xs text-slate-500">Made-up details, so you can see the shape of it.</p>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={resetting}
        onClose={() => setResetting(false)}
        onConfirm={() => reset.mutate()}
        loading={reset.isPending}
        tone="primary"
        confirmLabel="Use Edvora’s wording"
        title="Put this email back?"
        description="Your wording for this email is dropped, and Edvora’s is used again."
      />
    </>
  );
}
